"use client";

import { useState, useRef } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import {
  ArrowUpTrayIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/20/solid";

const API = "https://erp-xe9u.onrender.com/api";

// ─── Column definitions ───────────────────────────────────────────────────────
//
// MATERIAL columns:
//   defaultCost      = buy cost / default vendor price
//   Vendor1..10      = vendor name, price (buy cost), SKU
//   defaultVendor    = slot number 1–10
//
// LABOR columns:
//   defaultUnitRate  = billed rate (cost with burden) — stored as defaultUnitRate
//   defaultWorkerRate= paid to worker                 — stored as defaultCost
//   NO vendor columns — labor items are vendorless

const MATERIAL_VENDOR_COLS = [];
for (let i = 1; i <= 10; i++) {
  MATERIAL_VENDOR_COLS.push(`Vendor${i}`, `Vendor${i}Price`, `Vendor${i}SKU`);
}

const MATERIAL_HEADERS = [
  "itemCode",
  "description",
  "category",
  "sectionNumber",
  "uom",
  "size",
  "defaultVendor",
  ...MATERIAL_VENDOR_COLS,
  "manufacturer",
  "partNumber",
  "itemNotes",
  "isActive",
];

const LABOR_HEADERS = [
  "itemCode",
  "description",
  "category",
  "sectionNumber",
  "uom",
  "defaultUnitRate", // billed rate
  "defaultWorkerRate", // worker pay rate  (maps to defaultCost in the model)
  "manufacturer",
  "partNumber",
  "itemNotes",
  "isActive",
];

// ─── Type detection ───────────────────────────────────────────────────────────
function detectType(headers) {
  if (headers.includes("defaultUnitRate")) return "labor";
  if (headers.includes("Vendor1") || headers.includes("size"))
    return "material";
  return null;
}

// ─── Row normalizers ──────────────────────────────────────────────────────────
const toStr = (v) => String(v ?? "").trim();
const toNum = (v) => parseFloat(v) || 0;
const toBool = (v) => {
  if (v === false || v === 0) return false;
  if (typeof v === "string" && v.trim().toUpperCase() === "FALSE") return false;
  return true;
};

function normalizeMaterialRow(row) {
  const base = {
    itemCode: toStr(row.itemCode),
    description: toStr(row.description),
    category: "M",
    sectionNumber: toStr(row.sectionNumber),
    uom: toStr(row.uom) || "EA",
    size: toStr(row.size),
    defaultVendor:
      row.defaultVendor !== undefined && row.defaultVendor !== ""
        ? row.defaultVendor
        : "",
    manufacturer: toStr(row.manufacturer),
    partNumber: toStr(row.partNumber),
    itemNotes: toStr(row.itemNotes),
    isActive: toBool(row.isActive),
  };
  for (let i = 1; i <= 10; i++) {
    base[`Vendor${i}`] = toStr(row[`Vendor${i}`]);
    base[`Vendor${i}Price`] = toNum(row[`Vendor${i}Price`]);
    base[`Vendor${i}SKU`] = toStr(row[`Vendor${i}SKU`]);
  }
  return base;
}

function normalizeLaborRow(row) {
  return {
    itemCode: toStr(row.itemCode),
    description: toStr(row.description),
    category: "L",
    sectionNumber: toStr(row.sectionNumber),
    uom: toStr(row.uom) || "EA",
    defaultUnitRate: toNum(row.defaultUnitRate), // billed
    defaultWorkerRate: toNum(row.defaultWorkerRate), // paid — backend reads this as defaultCost
    manufacturer: toStr(row.manufacturer),
    partNumber: toStr(row.partNumber),
    itemNotes: toStr(row.itemNotes),
    isActive: toBool(row.isActive),
  };
}

// ─── Template sample data ─────────────────────────────────────────────────────
function buildMaterialSample() {
  const vendorSample = {};
  for (let i = 1; i <= 10; i++) {
    vendorSample[`Vendor${i}`] = i === 1 ? "Home Depot" : "";
    vendorSample[`Vendor${i}Price`] = i === 1 ? 0.51 : "";
    vendorSample[`Vendor${i}SKU`] = i === 1 ? "HD-D12PL" : "";
  }
  return [
    {
      itemCode: "D12PL",
      description: '1/2" Plain Drywall',
      category: "M",
      sectionNumber: "09 29 00",
      uom: "SF",
      size: "4'x12'",
      defaultVendor: 1,
      ...vendorSample,
      manufacturer: "USG",
      partNumber: "12345",
      itemNotes:
        "defaultVendor = vendor slot # (1–10); price comes from that vendor",
      isActive: true,
    },
  ];
}

function buildLaborSample() {
  return [
    {
      itemCode: "D12-HR 1112",
      description: "Drywall 1/2\" – High Rise 11' to 12'",
      category: "L",
      sectionNumber: "09 29 00",
      uom: "SF",
      defaultUnitRate: 0.57, // billed (with burden)
      defaultWorkerRate: 0.42, // paid to worker
      manufacturer: "",
      partNumber: "",
      itemNotes:
        "defaultUnitRate = billed. defaultWorkerRate = paid to worker.",
      isActive: true,
    },
  ];
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function ItemDatabaseUpload({ onUploadComplete, onClose }) {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null); // { rows, type, totalRows }
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  // ── Parse ──────────────────────────────────────────────────────────────────
  const parseExcelFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(new Uint8Array(e.target.result), {
            type: "array",
          });
          const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {
            defval: "",
          });
          if (!rows.length) return reject(new Error("Excel file is empty"));

          const headers = Object.keys(rows[0]);
          const type = detectType(headers);

          if (!type) {
            return reject(
              new Error(
                'Invalid file format. Expected material columns (size / Vendor1) or "defaultUnitRate" (labor).',
              ),
            );
          }

          const normalized =
            type === "material"
              ? rows.map(normalizeMaterialRow)
              : rows.map(normalizeLaborRow);

          // Basic validation — filter blank item codes
          const valid = normalized.filter((r) => r.itemCode);
          const skipped = normalized.length - valid.length;

          resolve({
            rows: valid,
            type,
            totalRows: valid.length,
            skippedBlank: skipped,
          });
        } catch (err) {
          reject(new Error("Failed to parse Excel file: " + err.message));
        }
      };
      reader.readAsArrayBuffer(file);
    });

  // ── File selection ─────────────────────────────────────────────────────────
  const handleFileSelect = async (selected) => {
    const name = selected.name.toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls")) {
      setParseError("Please select an Excel file (.xlsx or .xls)");
      return;
    }
    setFile(selected);
    setParseError(null);
    setUploadResult(null);
    try {
      const parsed = await parseExcelFile(selected);
      setParsedData(parsed);
    } catch (err) {
      setParseError(err.message);
      setParsedData(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  // ── Upload ─────────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!parsedData) return;
    setUploading(true);
    setUploadProgress({ status: "starting", percentComplete: 0 });

    try {
      const headers = getHeaders();
      const { jobId, total } = (
        await axios.post(
          `${API}/item-database/upload`,
          { items: parsedData.rows },
          { headers },
        )
      ).data;

      setUploadProgress({
        status: "processing",
        percentComplete: 0,
        total,
        created: 0,
        updated: 0,
        skipped: 0,
      });

      const poll = setInterval(async () => {
        try {
          const { data: s } = await axios.get(
            `${API}/item-database/upload-status/${jobId}`,
            { headers },
          );
          setUploadProgress({
            status: s.status,
            percentComplete: s.percentComplete,
            total: s.total,
            processed: s.processed,
            created: s.created,
            updated: s.updated,
            skipped: s.skipped,
            errors: s.errors,
            hasMoreErrors: s.hasMoreErrors,
          });
          if (s.status === "completed" || s.status === "failed") {
            clearInterval(poll);
            setUploading(false);
            setUploadProgress(null);
            if (s.status === "completed") {
              setUploadResult({
                success: true,
                results: {
                  created: s.created,
                  updated: s.updated,
                  skipped: s.skipped,
                },
              });
              if (onUploadComplete) onUploadComplete();
            } else {
              setUploadResult({
                success: false,
                message: s.errors?.[0]?.error || "Upload failed",
              });
            }
          }
        } catch (pollErr) {
          console.error("Poll error:", pollErr);
        }
      }, 2000);
    } catch (err) {
      setUploading(false);
      setUploadProgress(null);
      setUploadResult({
        success: false,
        message: err.response?.data?.message || err.message,
      });
    }
  };

  // ── Template download ──────────────────────────────────────────────────────
  const downloadTemplate = (type) => {
    const data =
      type === "material" ? buildMaterialSample() : buildLaborSample();
    const headers = type === "material" ? MATERIAL_HEADERS : LABOR_HEADERS;
    const ws = XLSX.utils.json_to_sheet(data, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      type === "material" ? "Material Items" : "Labor Items",
    );
    XLSX.writeFile(wb, `${type}_items_template.xlsx`);
  };

  const resetUpload = () => {
    setFile(null);
    setParsedData(null);
    setUploadResult(null);
    setParseError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-700 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-600">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <ArrowUpTrayIcon className="h-5 w-5 text-orange-400" />
            Upload Items from Excel
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Templates */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-300 mb-3">
              Download Templates
            </h3>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => downloadTemplate("material")}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
              >
                <DocumentArrowDownIcon className="h-4 w-4" /> Material Items
                Template
              </button>
              <button
                onClick={() => downloadTemplate("labor")}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded text-sm transition-colors"
              >
                <DocumentArrowDownIcon className="h-4 w-4" /> Labor Items
                Template
              </button>
            </div>
            {/* Column legend */}
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="p-3 bg-blue-900/20 border border-blue-800/40 rounded-lg">
                <p className="text-[11px] font-semibold text-blue-400 mb-1">
                  Material columns
                </p>
                <p className="text-[11px] text-gray-500">
                  itemCode, description, category, sectionNumber, uom,{" "}
                  <strong className="text-gray-400">size</strong>,{" "}
                  <strong className="text-gray-400">defaultCost</strong>,
                  defaultVendor (slot 1–10), Vendor1…10 + Price + SKU,
                  manufacturer, partNumber, itemNotes, isActive
                </p>
              </div>
              <div className="p-3 bg-orange-900/20 border border-orange-800/40 rounded-lg">
                <p className="text-[11px] font-semibold text-orange-400 mb-1">
                  Labor columns
                </p>
                <p className="text-[11px] text-gray-500">
                  itemCode, description, category, sectionNumber, uom,{" "}
                  <strong className="text-gray-400">defaultUnitRate</strong>{" "}
                  (billed),{" "}
                  <strong className="text-gray-400">defaultWorkerRate</strong>{" "}
                  (paid), manufacturer, partNumber, itemNotes, isActive
                </p>
              </div>
            </div>
          </div>

          {/* Drop zone */}
          {!uploadResult && (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? "border-orange-500 bg-orange-500/10"
                  : "border-gray-500 hover:border-gray-400"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) =>
                  e.target.files?.[0] && handleFileSelect(e.target.files[0])
                }
                className="hidden"
                id="file-upload"
              />
              {!file ? (
                <>
                  <DocumentArrowDownIcon className="h-12 w-12 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-300 mb-2">
                    Drag and drop your Excel file here, or
                  </p>
                  <label
                    htmlFor="file-upload"
                    className="inline-block px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded cursor-pointer text-sm transition-colors"
                  >
                    Browse Files
                  </label>
                </>
              ) : (
                <div>
                  <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-200 font-medium">{file.name}</p>
                  <button
                    onClick={resetUpload}
                    className="mt-2 text-sm text-red-400 hover:text-red-300"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Parse error */}
          {parseError && (
            <div className="mt-4 p-4 bg-red-900/30 border border-red-700 rounded-lg flex items-start gap-3">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-red-300 font-medium">Error parsing file</p>
                <p className="text-red-400 text-sm mt-1">{parseError}</p>
              </div>
            </div>
          )}

          {/* Upload progress */}
          {uploadProgress && !uploadResult && (
            <div className="mt-4 p-4 bg-gray-800 rounded-lg border border-gray-600">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-200">
                  {uploadProgress.status === "processing"
                    ? "Processing…"
                    : uploadProgress.status === "completed"
                      ? "Upload Complete"
                      : uploadProgress.status === "failed"
                        ? "Upload Failed"
                        : "Starting…"}
                </span>
                <span className="text-sm text-gray-400">
                  {uploadProgress.percentComplete || 0}%
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    uploadProgress.status === "failed"
                      ? "bg-red-500"
                      : uploadProgress.status === "completed"
                        ? "bg-green-500"
                        : "bg-orange-500"
                  }`}
                  style={{ width: `${uploadProgress.percentComplete || 0}%` }}
                />
              </div>
              {uploadProgress.processed !== undefined && (
                <div className="mt-3 grid grid-cols-5 gap-2 text-sm">
                  {[
                    {
                      label: "Processed",
                      val: uploadProgress.processed || 0,
                      color: "text-white",
                    },
                    {
                      label: "Total",
                      val: uploadProgress.total || 0,
                      color: "text-gray-300",
                    },
                    {
                      label: "Created",
                      val: uploadProgress.created || 0,
                      color: "text-green-400",
                    },
                    {
                      label: "Updated",
                      val: uploadProgress.updated || 0,
                      color: "text-blue-400",
                    },
                    {
                      label: "Skipped",
                      val: uploadProgress.skipped || 0,
                      color: "text-red-400",
                    },
                  ].map(({ label, val, color }) => (
                    <div
                      key={label}
                      className="text-center p-2 bg-gray-700 rounded"
                    >
                      <div className={`font-semibold ${color}`}>{val}</div>
                      <div className="text-gray-400 text-xs">{label}</div>
                    </div>
                  ))}
                </div>
              )}
              {uploadProgress.errors?.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm text-red-400 font-medium mb-1">
                    Errors (
                    {uploadProgress.hasMoreErrors
                      ? "30+"
                      : uploadProgress.errors.length}
                    ):
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {uploadProgress.errors.map((err, idx) => (
                      <div
                        key={idx}
                        className="text-xs text-red-300 bg-red-900/30 px-2 py-1 rounded"
                      >
                        Row {err.row}
                        {err.itemCode ? ` (${err.itemCode})` : ""}: {err.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preview */}
          {parsedData && !uploadResult && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <h3 className="text-sm font-medium text-gray-300">
                    Preview ({parsedData.totalRows} items)
                  </h3>
                  {parsedData.skippedBlank > 0 && (
                    <span className="text-xs text-yellow-400 bg-yellow-900/30 border border-yellow-800/40 px-2 py-0.5 rounded">
                      {parsedData.skippedBlank} blank rows skipped
                    </span>
                  )}
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded ${
                    parsedData.type === "material"
                      ? "bg-blue-900/50 text-blue-300"
                      : "bg-orange-900/50 text-orange-300"
                  }`}
                >
                  {parsedData.type === "material"
                    ? "Material Items"
                    : "Labor Items"}
                </span>
              </div>

              <div className="bg-gray-800 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-900 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs text-gray-400">
                        Item Code
                      </th>
                      <th className="px-3 py-2 text-left text-xs text-gray-400">
                        Description
                      </th>
                      <th className="px-3 py-2 text-left text-xs text-gray-400">
                        Section
                      </th>
                      <th className="px-3 py-2 text-left text-xs text-gray-400">
                        UoM
                      </th>
                      {parsedData.type === "material" ? (
                        <>
                          <th className="px-3 py-2 text-left text-xs text-gray-400">
                            Size
                          </th>
                          <th className="px-3 py-2 text-right text-xs text-gray-400">
                            Cost
                          </th>
                        </>
                      ) : (
                        <>
                          <th className="px-3 py-2 text-right text-xs text-gray-400">
                            Unit Rate
                          </th>
                          <th className="px-3 py-2 text-right text-xs text-gray-400">
                            Worker Rate
                          </th>
                        </>
                      )}
                      <th className="px-3 py-2 text-center text-xs text-gray-400">
                        Active
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {parsedData.rows.slice(0, 10).map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-750">
                        <td className="px-3 py-2 text-gray-200 font-mono text-xs">
                          {row.itemCode}
                        </td>
                        <td className="px-3 py-2 text-gray-300 truncate max-w-xs">
                          {row.description}
                        </td>
                        <td className="px-3 py-2 text-gray-400 font-mono text-xs">
                          {row.sectionNumber || "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-400">{row.uom}</td>
                        {parsedData.type === "material" ? (
                          <>
                            <td className="px-3 py-2 text-gray-400 text-xs">
                              {row.size || "—"}
                            </td>
                            <td className="px-3 py-2 text-gray-200 text-right font-mono">
                              {(() => {
                                const slot = parseInt(row.defaultVendor);
                                const p =
                                  slot >= 1 ? row[`Vendor${slot}Price`] : 0;
                                return `$${toNum(p).toFixed(3)}`;
                              })()}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-3 py-2 text-blue-400 text-right font-mono">
                              ${toNum(row.defaultUnitRate).toFixed(3)}
                            </td>
                            <td className="px-3 py-2 text-orange-400 text-right font-mono">
                              ${toNum(row.defaultWorkerRate).toFixed(3)}
                            </td>
                          </>
                        )}
                        <td className="px-3 py-2 text-center">
                          <span
                            className={`text-xs font-semibold ${row.isActive ? "text-green-400" : "text-gray-500"}`}
                          >
                            {row.isActive ? "Yes" : "No"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedData.totalRows > 10 && (
                  <p className="text-xs text-gray-600 text-center py-2 border-t border-gray-700">
                    …and {parsedData.totalRows - 10} more rows
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Result */}
          {uploadResult && (
            <div className="mt-4">
              {uploadResult.success ? (
                <div className="p-4 bg-green-900/30 border border-green-700 rounded-lg flex items-start gap-3">
                  <CheckCircleIcon className="h-6 w-6 text-green-400 shrink-0" />
                  <div className="flex-1">
                    <p className="text-green-300 font-medium">
                      Upload Complete!
                    </p>
                    <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
                      {[
                        {
                          label: "Created",
                          val: uploadResult.results.created,
                          color: "text-green-400",
                          bg: "bg-green-900/30",
                        },
                        {
                          label: "Updated",
                          val: uploadResult.results.updated,
                          color: "text-blue-400",
                          bg: "bg-blue-900/30",
                        },
                        {
                          label: "Skipped",
                          val: uploadResult.results.skipped,
                          color: "text-yellow-400",
                          bg: "bg-yellow-900/30",
                        },
                      ].map(({ label, val, color, bg }) => (
                        <div
                          key={label}
                          className={`${bg} rounded p-2 text-center`}
                        >
                          <p className={`text-2xl font-bold ${color}`}>{val}</p>
                          <p className={`${color} text-xs opacity-80`}>
                            {label}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg flex items-start gap-3">
                  <ExclamationCircleIcon className="h-6 w-6 text-red-400 shrink-0" />
                  <div>
                    <p className="text-red-300 font-medium">Upload Failed</p>
                    <p className="text-red-400 text-sm mt-1">
                      {uploadResult.message}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer buttons */}
          <div className="mt-6 flex justify-end gap-3">
            {uploadResult ? (
              <>
                <button
                  onClick={resetUpload}
                  className="px-4 py-2 text-sm text-gray-300 bg-gray-600 rounded hover:bg-gray-500 transition-colors"
                >
                  Upload Another
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-white bg-orange-500 rounded hover:bg-orange-600 transition-colors"
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-gray-300 bg-gray-600 rounded hover:bg-gray-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!parsedData || uploading}
                  className={`px-6 py-2 text-sm text-white rounded transition-colors flex items-center gap-2 ${
                    !parsedData || uploading
                      ? "bg-gray-500 cursor-not-allowed"
                      : "bg-orange-500 hover:bg-orange-600"
                  }`}
                >
                  {uploading ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Uploading…
                    </>
                  ) : (
                    <>
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      Upload {parsedData?.totalRows || 0} Items
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
